const API_URL = 'https://myblrc.in/api/api.php';

export class BlogApiError extends Error {
  constructor(message, fields) {
    super(message);
    this.name = 'BlogApiError';
    this.fields = fields || null;
  }
}

function adminToken() {
  return localStorage.getItem('adminToken') || '';
}

async function request(action, { method = 'GET', query = {}, body } = {}) {
  const token = adminToken();
  const params = new URLSearchParams({ xAction: action, _t: Date.now().toString() });
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value) !== '') {
      params.set(key, String(value));
    }
  });
  if (token) {
    params.set('token', token);
  }

  const headers = {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Authorization: `Bearer ${token}`,
  };

  let payload;
  if (body instanceof FormData) {
    if (token && !body.has('token')) {
      body.append('token', token);
    }
    payload = body;
  } else if (body) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify({ ...body, token });
  }

  const response = await fetch(`${API_URL}?${params.toString()}`, {
    method,
    headers,
    body: payload,
    cache: 'no-store',
  });

  const text = await response.text();
  let result;
  try {
    result = JSON.parse(text);
  } catch (error) {
    throw new BlogApiError('Server returned an invalid response. The blog API may not be deployed yet.');
  }

  if (result.status === 'error' || result.data?.status === 'error') {
    let message = result.data?.message || result.message || 'Request failed';
    if (/invalid action/i.test(message)) {
      message = 'The blog API is not deployed yet. Upload the api folder so myblrc.in serves the updated api.php and blog_api.php. The database tables are already created.';
    }
    throw new BlogApiError(message, result.data?.fields || null);
  }

  return result.data || result;
}

export function imageSrc(blog) {
  if (!blog) return '';
  if (blog.featured_image_url) return blog.featured_image_url;
  if (!blog.featured_image) return '';
  if (/^https?:\/\//i.test(blog.featured_image)) return blog.featured_image;
  return `${API_URL.replace(/api\.php$/, '')}${String(blog.featured_image).replace(/^\//, '')}`;
}

async function localReadRequest(action, { method = 'GET', query = {}, body } = {}) {
  if (process.env.NODE_ENV !== 'development') return null;
  const params = new URLSearchParams({ xAction: action });
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value) !== '') params.set(key, String(value));
  });
  const token = adminToken();
  if (token) params.set('token', token);
  try {
    const response = await fetch(`/api/api.php?${params.toString()}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    const result = await response.json();
    if (!response.ok || result?.data?.status === 'error') return null;
    return result.data;
  } catch (error) {
    return null;
  }
}

function mergeReadStats(blog, stats) {
  const match = (stats?.blogs || []).find((item) => Number(item.id) === Number(blog.id));
  return match ? { ...blog, ...match } : blog;
}

export async function getBlogs({ page = 1, limit = 10, search = '', category = '', status = 'all' } = {}) {
  const data = await request('getBlogs', {
    query: { page, limit, search, category, status: status || 'all' },
  });
  const stats = await localReadRequest('getBlogReadStats');
  if (stats && Array.isArray(data.blogs)) {
    data.blogs = data.blogs.map((blog) => mergeReadStats(blog, stats));
  }
  return data;
}

export async function getBlog(id) {
  const data = await request('getBlog', { query: { id } });
  const stats = await localReadRequest('getBlogReadStats', { query: { id } });
  if (stats && data.blog) data.blog = mergeReadStats(data.blog, stats);
  return data;
}

export async function saveBlog(payload) {
  const data = await request('saveBlog', { method: 'POST', body: payload });
  if (data?.blog?.id) {
    await localReadRequest('setBlogReadVisibility', {
      method: 'POST',
      body: { id: data.blog.id, show_read_count: payload.show_read_count },
    });
    const stats = await localReadRequest('getBlogReadStats', { query: { id: data.blog.id } });
    if (stats) data.blog = mergeReadStats(data.blog, stats);
  }
  return data;
}

export function deleteBlog(id) {
  return request('deleteBlog', { method: 'POST', body: { id } });
}

export function setBlogStatus(id, status) {
  return request('setBlogStatus', { method: 'POST', body: { id, status } });
}

export async function setBlogReadVisibility(id, showReadCount) {
  const body = { id, show_read_count: showReadCount ? 1 : 0 };
  if (process.env.NODE_ENV === 'development') {
    const params = new URLSearchParams({ xAction: 'setBlogReadVisibility' });
    const token = adminToken();
    if (token) params.set('token', token);
    let result;
    try {
      const response = await fetch(`/api/api.php?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      result = await response.json();
      if (!response.ok || result?.data?.status === 'error' || result?.status === 'error') {
        throw new BlogApiError(result?.data?.message || result?.message || 'Unable to save reader count visibility.');
      }
    } catch (error) {
      if (error instanceof BlogApiError) throw error;
      throw new BlogApiError('Unable to save reader count visibility.');
    }
    return result.data;
  }
  return request('setBlogReadVisibility', { method: 'POST', body });
}

export function uploadBlogImage(file) {
  const body = new FormData();
  body.append('image', file);
  return request('uploadBlogImage', { method: 'POST', body });
}

export function deleteBlogImage(path) {
  return request('deleteBlogImage', { method: 'POST', body: { path } });
}

export function getBlogCategories() {
  return request('getBlogCategories');
}
